import React, { memo, useContext, useEffect, useState } from 'react'
import {
  AppShell,
  Burger,
  Center,
  Title,
  Space,
  Container,
  Stack,
  Avatar,
  Flex,
  Menu,
  Image,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Outlet } from "react-router-dom";
import classes from "./AppShell.module.css";
import CustomDropdown from './CustomDropdown/Customdropdown.component';
import { authContext } from "../../context/AuthProvider.component";
import DynamicBreadcrumbs from './Breadcrumb/Breadcrumb.component';
import { FiPieChart } from "react-icons/fi";
import { LuFileChartColumn } from "react-icons/lu";
import { HiOutlineArchiveBox } from "react-icons/hi2";
import { IoExtensionPuzzleOutline } from "react-icons/io5";
import { IoCubeOutline } from "react-icons/io5";
import { TbCubePlus } from "react-icons/tb";
import { LiaTruckMovingSolid } from "react-icons/lia";
// import { MdOutlineLocationOn } from "react-icons/md";
import { VscNewFile } from "react-icons/vsc";
import { LiaSaveSolid } from "react-icons/lia";
import { LiaPalletSolid } from "react-icons/lia";
import { LiaProductHunt } from "react-icons/lia";
import { BsBoxes } from "react-icons/bs";
import { IoPersonOutline } from "react-icons/io5";
import { TbBuildingFactory2 } from "react-icons/tb";
import { GoDatabase } from "react-icons/go";
import { GiProcessor } from "react-icons/gi";
import { LuRecycle } from "react-icons/lu";

const BasicAppShell = () => {
  const [opened, { toggle }] = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const crtUrl = location.pathname;

  // Update active state based on current URL
  const [activeItem, setActiveItem] = useState("dashboard");

  useEffect(() => {
    // Extract the main path segment without leading slash
    const path = crtUrl.split('/')[1] || 'dashboard';
    setActiveItem(path);
  }, [crtUrl]);

  const { handleLogout, getUserRole, isAuthenticated, auth }: any = useContext(authContext);
  const token = localStorage.getItem("token");
  const role = getUserRole(); // Use secure role from context instead of localStorage
  
  useEffect(() => {
    // Only redirect if not loading and not authenticated
    if (!auth.loading && !isAuthenticated()) {
      navigate("/");
    }
  }, [navigate, isAuthenticated, auth.loading]);

  const AdminControlitems = [
    { label: "Category", href: "/manage-verticals", icon: <HiOutlineArchiveBox size={20} />, id: "manage-verticals" },
    { label: "Sub Category", href: "/manage-products-category", icon: <IoCubeOutline size={20} />, id: "manage-products-category" },
    { label: "Raw Material", href: "/manage-raw-material", icon: <IoExtensionPuzzleOutline size={20} />, id: "manage-raw-material" },
    { label: "Material Sub Type", href: "/material-subtype", icon: <TbCubePlus size={20} />, id: "material-subtype" },
    // { label: "Location", href: "/location", icon: <MdOutlineLocationOn size={20} />, id: "location" },
    { label: "Transport", href: "/transport", icon: <LiaTruckMovingSolid size={20} />, id: "transport" },
    { label: "Pallets", href: "/pallets", icon: <LiaPalletSolid size={20} />, id: "pallets" },
    { label: "Packaging", href: "/package-box", icon: <BsBoxes size={20} />, id: "package-box" },
    { label: "Products", href: "/manage-products", icon: <LiaProductHunt size={20} />, id: "manage-products" },
    { label: "Customers", href: "/manage-customers", icon: <IoPersonOutline size={20} />, id: "manage-customers" },
    { label: "Factory", href: "/manage-factory", icon: <TbBuildingFactory2 size={20} />, id: "manage-factory" },
    { label: "Process", href: "/manage-process", icon: <GiProcessor size={20} />, id: "manage-process" },
    { label: "DB Version", href: "/manage-db-version", icon: <GoDatabase size={20} />, id: "manage-db-version" },
    { label: "Disposal", href: "/disposal", icon: <LuRecycle size={20} />, id: "disposal" },
  ];

  const ComputeLcaitems = [
    { label: "New LCA", href: "/newlca", icon: <VscNewFile size={20} />, id: "newlca" },
    { label: "Draft", href: "/draft", icon: <LiaSaveSolid size={20} />, id: "draft" },
  ];

  // Check if an item is active based on URL
  const isItemActive = (path: any) => {
    const itemPath = path.replace('/', '');
    return activeItem === itemPath;
  };

  return (
    <AppShell
      navbar={{ width: 255, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Navbar className={classes.appShellContainer}>
        <div className={classes.navbar}>
          {/* Fixed header */}
          <div className={classes.header}>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              style={{ position: 'absolute', top: '26px', right: '-22%' }}
            />
            <Center mt={10} pt={10}>
              <Image
                radius="md"
                h={22}
                w="auto"
                fit="contain"
                src={'https://www.mainetti.com/wp-content/uploads/2023/03/Logo_Mainetti_ThickerStroke_022323.svg'}
              />
            </Center>
          </div>

          {/* Scrollable navigation content */}
          <div className={classes.navbarMain}>
            <Link
              className={classes.link}
              data-active={'/dashboard' === crtUrl || undefined}
              to='/dashboard'
              onClick={() => setActiveItem('dashboard')}
            >
              <div className={classes.linkContainer}>
                <div
                  className={classes.linkWrapper}
                  data-active={isItemActive('dashboard')}
                />
                <div className={classes.linkIcon}><FiPieChart size={20} /></div>
                <span className={classes.menuName}>Dashboard</span>
              </div>
            </Link>

            {/* Updated to pass activeItem and setActiveItem */}
            <CustomDropdown
              buttonLabel="Compute LCA"
              items={ComputeLcaitems}
            />

            <Link
              className={classes.link}
              data-active={'/lca-report' === crtUrl || undefined}
              to='/lca-report'
              onClick={() => setActiveItem('lacreport')}
            >
              <div className={classes.linkContainer}>
                <div
                  className={classes.linkWrapper}
                  data-active={isItemActive('lacreport')}
                />
                <div className={classes.linkIcon}><LuFileChartColumn size={20} /></div>
                <span className={classes.menuName}>LCA Report</span>
              </div>
            </Link>

            {/* Updated to pass activeItem and setActiveItem */}
            {role === 'LCA-Admin' && 
            <CustomDropdown
              buttonLabel="Admin Control"
              items={AdminControlitems}
            />
            }
          </div>
        </div>
      </AppShell.Navbar>
      <AppShell.Main className={classes.main}>
        <Container className={classes.container}>
          <Space h="" />
          <Stack bg="transparent" gap="md">
            <Flex
              align="center"
              direction={{ base: "row", sm: "row" }}
              gap={{ base: "sm", sm: "lg" }}
              justify="space-between"
              className={classes.topnavbar}
            >
              <Title></Title>
              <Menu width={200} shadow="md">
                <Menu.Target>
                  <Avatar
                    component="div"
                    src=""
                    alt="it's me"
                    style={{ cursor: "pointer" }}
                  />
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item component="div" onClick={handleLogout}>Logout</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Flex>
            <DynamicBreadcrumbs />
            <Outlet />
          </Stack>
        </Container>
      </AppShell.Main>
    </AppShell>
  );
};

export default memo(BasicAppShell);